import { ClipboardEvent, ReactNode, SyntheticEvent, useEffect, useId, useRef, useState } from "react";
import MyLocationRoundedIcon from "@mui/icons-material/MyLocationRounded";
import { Modal } from "../Modal";
import { Button } from "../Button";
import { Field, Input } from "../Field";
import { Notice } from "../Notice";
import { MapPreview } from "../MapPreview";
import { textDir } from "../textDir";
import { useNextT } from "../../i18n";
import s from "./CoordinatesDialog.module.scss";

export type CoordinatesDialogLabels = {
  /** "Manual address" */
  title: string;
  /** "The address text stays as you typed it. The coordinates only place the map pin." */
  intro: string;
  /** "Close without saving" (the ✕) */
  closeLabel: string;
  /** "Address text" + its muted note "(shown to everyone, exactly as written)" */
  text: string; textNote: string; textRequired: string;
  latitude: string; longitude: string;
  /** Shown for an empty, non-numeric or out-of-range box (−90..90 / −180..180). */
  latitudeError: ReactNode; longitudeError: ReactNode;
  /** "Tip: paste both at once, like 40.6695, -73.9411 …" (phone: "You can paste … into either box.") */
  pasteTip: ReactNode;
  /** "Wrong spot? Check the order: latitude first (40.…), then longitude (-73.…)." */
  wrongSpot: ReactNode;
  /** The preview's name for screen readers, e.g. "Map preview: pin at 40.6695, -73.9411". */
  mapLabel: (lat: number, lng: number) => string;
  useMyLocation: string; useMyLocationHint: string;
  /** Refused or failed: an inline notice, the boxes stay as they were. */
  locationFailed: string;
  /** "Save manual address" */
  save: string;
};

export type CoordinatesDialogProps = {
  open: boolean;
  /** The address text handed over (the typed search text, or "" from the "Manual address (coordinates)" button). */
  initialText: string;
  initialLat?: number | null;
  initialLng?: number | null;
  onSave: (v: { text: string; latitude: number; longitude: number }) => void;
  onClose: () => void;
  /** Owner decision Q5: phones only — the caller passes `usePhone()`. Also needs `navigator.geolocation`. */
  showUseMyLocation: boolean;
  labels: CoordinatesDialogLabels;
};

const NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)$/;
/** "40.6695, -73.9411" (as Google Maps copies it), also with a space or semicolon between. */
const PAIR = /^\s*([+-]?\d+(?:\.\d+)?)\s*[,;\s]\s*([+-]?\d+(?:\.\d+)?)\s*$/;

const parse = (v: string, limit: number): number | null => {
  const t = v.trim();
  if (!NUMBER.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) && Math.abs(n) <= limit ? n : null;
};
const numberLike = (v: string) => NUMBER.test(v.trim());
const asText = (n: number | null | undefined) => (n === null || n === undefined ? "" : String(n));
/** Typing adds one character at a time; a pair that arrives in one change was pasted, dropped or autofilled. */
const manyAtOnce = (before: string, after: string) => after.length - before.length > 1;
/** Six decimals (about 10 cm), no trailing zeros. */
const fix6 = (n: number) => String(Number(n.toFixed(6)));

/** Board 31 A / 32: address text + latitude/longitude, split on paste, range-checked, previewed. A bottom sheet on phones (Modal). */
export const CoordinatesDialog = ({ open, initialText, initialLat, initialLng, onSave, onClose, showUseMyLocation, labels }: CoordinatesDialogProps) => {
  const { t } = useNextT();
  const id = useId();
  const [text, setText] = useState(initialText);
  const [lat, setLat] = useState(asText(initialLat));
  const [lng, setLng] = useState(asText(initialLng));
  const [touched, setTouched] = useState({ text: false, lat: false, lng: false });
  const [submitted, setSubmitted] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationFailed, setLocationFailed] = useState(false);
  const textRef = useRef<HTMLInputElement>(null);
  const latRef = useRef<HTMLInputElement>(null);
  const lngRef = useRef<HTMLInputElement>(null);
  const alive = useRef(true);
  // Bumped on every opening and closing: a location answer for an earlier opening is dropped.
  const generation = useRef(0);
  // Set on every mount: StrictMode runs the cleanup once and mounts again.
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  /** The last valid pin while open (the preview keeps it while the boxes hold something else). */
  const lastPin = useRef<{ lat: number; lng: number } | null>(null);
  // Every opening starts from what the caller hands over (adjust state while rendering, so no stale frame shows).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    generation.current++;
    lastPin.current = null;
    if (open) {
      setText(initialText); setLat(asText(initialLat)); setLng(asText(initialLng));
      setTouched({ text: false, lat: false, lng: false }); setSubmitted(false); setLocating(false); setLocationFailed(false);
    }
  }

  const latN = parse(lat, 90);
  const lngN = parse(lng, 180);
  // The map preview, once shown, stays mounted while the dialog is open (hidden while the pair is not valid) and only
  // moves its pin: every new map is a billed map load.
  if (latN !== null && lngN !== null) lastPin.current = { lat: latN, lng: lngN };
  const pin = lastPin.current;
  const pinShown = latN !== null && lngN !== null;
  const textOk = text.trim() !== "";
  const show = (value: string, ok: boolean, wasTouched: boolean) => !ok && (submitted || (value.trim() !== "" && (wasTouched || numberLike(value))));
  const textErr = !textOk && (submitted || touched.text) ? labels.textRequired : undefined;
  const latErr = show(lat, latN !== null, touched.lat) ? labels.latitudeError : undefined;
  const lngErr = show(lng, lngN !== null, touched.lng) ? labels.longitudeError : undefined;

  /** A pasted (or dropped / autofilled: more than one character at once) "lat, lng" pair fills both boxes. */
  const splitPair = (v: string): boolean => {
    const m = PAIR.exec(v);
    if (!m) return false;
    setLat(m[1]); setLng(m[2]);
    return true;
  };
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (splitPair(e.clipboardData?.getData("text") ?? "")) e.preventDefault();
  };

  const canLocate = showUseMyLocation && typeof navigator !== "undefined" && !!navigator.geolocation;
  const locate = () => {
    setLocating(true); setLocationFailed(false);
    const gen = generation.current;
    const current = () => alive.current && gen === generation.current;
    navigator.geolocation.getCurrentPosition(
      (pos) => { if (!current()) return; setLocating(false); setLat(fix6(pos.coords.latitude)); setLng(fix6(pos.coords.longitude)); },
      () => { if (!current()) return; setLocating(false); setLocationFailed(true); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  };

  const save = (e?: SyntheticEvent) => {
    e?.preventDefault();
    setSubmitted(true);
    if (!textOk) { textRef.current?.focus(); return; }
    if (latN === null) { latRef.current?.focus(); return; }
    if (lngN === null) { lngRef.current?.focus(); return; }
    onSave({ text: text.trim(), latitude: latN, longitude: lngN });
  };

  const introId = `${id}-intro`;
  const tipId = `${id}-tip`;
  const hintId = `${id}-loc`;
  const both = (d?: string) => [d, tipId].filter(Boolean).join(" ");
  return (
    <Modal open={open} title={labels.title} onClose={onClose} size="sm" closeLabel={labels.closeLabel} describedBy={introId}
      actions={<><Button onClick={onClose}>{t("ui.cancel")}</Button><Button variant="primary" onClick={() => save()}>{labels.save}</Button></>}>
      <form className={s.form} onSubmit={save} noValidate
        // Enter in a box saves, like the primary button (the button sits in the dialog footer, outside the form).
        onKeyDown={(e) => { if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT" && !e.nativeEvent.isComposing) save(e); }}>
        <p id={introId} className={s.help}>{labels.intro}</p>
        <Field label={labels.text} optional={labels.textNote} error={textErr}>
          {(p) => <Input ref={textRef} id={p.id} aria-describedby={p.describedBy} invalid={p.invalid} value={text} dir={textDir(text)} autoComplete="off"
            onChange={(e) => setText(e.target.value)} onBlur={() => setTouched((x) => ({ ...x, text: true }))} />}
        </Field>
        {canLocate && (
          <div className={s.locate}>
            <Button fullWidth icon={<MyLocationRoundedIcon />} loading={locating} onClick={locate} aria-describedby={hintId}>{labels.useMyLocation}</Button>
            <p id={hintId} className={s.help}>{labels.useMyLocationHint}</p>
            {locationFailed && <Notice tone="warning">{labels.locationFailed}</Notice>}
          </div>
        )}
        <div className={s.pair}>
          <Field label={labels.latitude} error={latErr}>
            {(p) => <Input ref={latRef} id={p.id} aria-describedby={both(p.describedBy)} invalid={p.invalid} value={lat} dir="ltr" inputMode="decimal" autoComplete="off"
              onPaste={onPaste} onChange={(e) => { const v = e.target.value; if (!(manyAtOnce(lat, v) && splitPair(v))) setLat(v); }} onBlur={() => setTouched((x) => ({ ...x, lat: true }))} />}
          </Field>
          <Field label={labels.longitude} error={lngErr}>
            {(p) => <Input ref={lngRef} id={p.id} aria-describedby={both(p.describedBy)} invalid={p.invalid} value={lng} dir="ltr" inputMode="decimal" autoComplete="off"
              onPaste={onPaste} onChange={(e) => { const v = e.target.value; if (!(manyAtOnce(lng, v) && splitPair(v))) setLng(v); }} onBlur={() => setTouched((x) => ({ ...x, lng: true }))} />}
          </Field>
        </div>
        <p id={tipId} className={s.help}>{labels.pasteTip}</p>
        {pin && (
          <div hidden={!pinShown} className={s.preview}>
            <MapPreview lat={pin.lat} lng={pin.lng} label={labels.mapLabel(pin.lat, pin.lng)} />
            <p className={s.help}>{labels.wrongSpot}</p>
          </div>
        )}
      </form>
    </Modal>
  );
};
